import { useTranslation } from 'react-i18next'
import { useEffect, useMemo, useState } from 'react'
import { Skeleton } from 'components/UnvUI'
import { Button, TextField, Dialog, DialogTitle, DialogContent, DialogActions, MenuItem } from '@mui/material'

import $api from 'http/index'
import Notification from 'utils/Notifications'

import styles from './DistanceLearningPage.module.scss'

const fmtDuration = (sec) => {
  if (!sec) return '—'
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${m}:${String(s).padStart(2, '0')}`
}

const VideoLecturesPage = () => {
  const { t } = useTranslation('common')

  const [lectures, setLectures] = useState([])
  const [subjects, setSubjects] = useState([])
  const [subjectFilter, setSubjectFilter] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [playing, setPlaying] = useState(null)
  const [me, setMe] = useState(null)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [form, setForm] = useState({ title: '', subject: '', file: null })
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })

  const isTeacher = useMemo(() => {
    if (!me) return false
    const roles = me.roles || []
    return roles.includes('teacher') || me.primary_role === 'teacher' || roles.includes('lecturer')
  }, [me])

  const loadLectures = async () => {
    setLoading(true)
    setError(false)
    try {
      const res = await $api.get('/distance/lectures/', {
        params: subjectFilter ? { subject: subjectFilter } : {},
      })
      setLectures(res.data?.results || res.data || [])
    } catch {
      setError(true)
      setLectures([])
    } finally {
      setLoading(false)
    }
  }

  const loadSubjects = async () => {
    try {
      const res = await $api.get('/academic/subjects/')
      setSubjects(res.data?.results || res.data || [])
    } catch {
      setSubjects([])
    }
  }

  const loadMe = async () => {
    try {
      const res = await $api.get('/admin-panel/me/')
      setMe(res.data)
    } catch {
      setMe(null)
    }
  }

  useEffect(() => {
    loadMe()
    loadSubjects()
  }, [])

  useEffect(() => {
    loadLectures()
  }, [subjectFilter])

  const share = (lecture) => {
    const url = window.location.origin + '/distance/lectures/?id=' + lecture.id
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url)
      setNotify({ isOpen: true, message: t('auto.videolectures.link_copied', 'Ссылка скопирована'), type: 'success', sound: 'success' })
    } else {
      window.prompt(t('auto.videolectures.copy_link', 'Скопируйте ссылку'), url)
    }
  }

  const upload = async () => {
    if (!form.title || !form.file) {
      setNotify({ isOpen: true, message: t('auto.videolectures.fill_required', 'Заполните название и выберите файл'), type: 'warning', sound: 'warning' })
      return
    }
    try {
      const fd = new FormData()
      fd.append('title', form.title)
      if (form.subject) fd.append('subject', form.subject)
      fd.append('file', form.file)
      await $api.post('/distance/lectures/', fd)
      setNotify({ isOpen: true, message: t('auto.videolectures.uploaded', 'Лекция загружена'), type: 'success', sound: 'success' })
      setUploadOpen(false)
      setForm({ title: '', subject: '', file: null })
      loadLectures()
    } catch {
      setNotify({ isOpen: true, message: t('auto.videolectures.upload_error', 'Ошибка загрузки'), type: 'error', sound: 'error' })
    }
  }

  return (
    <div className={styles.page}>
      <div className={`unv-card ${styles.header}`}>
        <h1 className={styles.title}>{t('distance.card_lectures')}</h1>
        {isTeacher && (
          <Button variant="contained" onClick={() => setUploadOpen(true)}>
            {t('auto.videolectures.upload_lecture', 'Загрузить лекцию')}
          </Button>
        )}
      </div>

      <div className={`unv-card ${styles.toolbar}`}>
        <label className="unv-label">{t('schedule.subject')}</label>
        <select
          className="unv-select"
          value={subjectFilter}
          onChange={(e) => setSubjectFilter(e.target.value)}
        >
          <option value="">{t('common.all')}</option>
          {subjects.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      <div className={`unv-card ${styles.content}`}>
        {loading ? (
          <div className={styles.loaderWrap}>
            <Skeleton variant="card" />
          </div>
        ) : error ? (
          <div className="unv-empty">{t('schedule.section_in_dev')}</div>
        ) : lectures.length === 0 ? (
          <div className="unv-empty">{t('distance.no_lectures')}</div>
        ) : (
          <div className={styles.lectureList}>
            {lectures.map((l) => (
              <div key={l.id} className={`unv-card ${styles.lectureCard}`}>
                <div className={styles.lectureTitle}>{l.title || t('auto.videolectures.no_title', 'Без названия')}</div>
                <div className={styles.lectureMeta}>
                  {l.subject_name || l.subject?.name || t('auto.videolectures.no_subject', 'Без дисциплины')} ·{' '}
                  {fmtDuration(l.duration)}
                </div>
                <div className={styles.lectureActions}>
                  <button className="unv-btn" onClick={() => setPlaying(l)}>
                    {t('auto.videolectures.watch', 'Смотреть')}
                  </button>
                  <button className="unv-btn unv-btn--ghost" onClick={() => share(l)}>
                    {t('auto.videolectures.share', 'Поделиться')}
                  </button>
                </div>
                {playing?.id === l.id && (l.video_url || l.file) && (
                  <div className={styles.player}>
                    <video
                      src={l.video_url || l.file}
                      controls
                      autoPlay
                      style={{ width: '100%', display: 'block' }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('auto.videolectures.upload_lecture_title', 'Загрузка лекции')}</DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label={t('common.name')}
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            margin="normal"
          />
          <TextField
            select
            fullWidth
            label={t('schedule.subject')}
            value={form.subject}
            onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))}
            margin="normal"
          >
            <MenuItem value="">{t('auto.videolectures.not_selected', 'Не выбрано')}</MenuItem>
            {subjects.map((s) => (
              <MenuItem key={s.id} value={s.id}>
                {s.name}
              </MenuItem>
            ))}
          </TextField>
          <input
            type="file"
            accept="video/*"
            onChange={(e) => setForm((f) => ({ ...f, file: e.target.files?.[0] || null }))}
            style={{ marginTop: 12 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>{t('auto.videolectures.cancel', 'Отмена')}</Button>
          <Button variant="contained" onClick={upload}>
            {t('auto.videolectures.upload', 'Загрузить')}
          </Button>
        </DialogActions>
      </Dialog>

      <Notification notify={notify} setNotify={setNotify} />
    </div>
  )
}

export default VideoLecturesPage
